import { useState, useEffect, useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

import MDBox from "components/MDBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

import {
  buildRoutes,
  landingPageRoute,
  signInCoverRoute,
  signUpCoverRoute,
  detailLayananRoute,
  detailPengajuanRoute,
  verifikasiPublikRoute,
} from "routes";
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";
import { useSiteAssets } from "context/SiteAssetsContext";
import { SITE_ASSET_KEYS } from "utils/siteAssets";
import { getCurrentUser } from "actions/AuthAction";
import { getDashboardPath } from "services/api";

import defaultBrand from "assets/images/logorote.png";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const { getAssetUrl } = useSiteAssets();
  const brandLogo = getAssetUrl(SITE_ASSET_KEYS.LOGO_APLIKASI, defaultBrand);

  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const filterAvailableRoutes = (allRoutes) =>
    allRoutes
      .filter((route) => {
        if (route.available === false) return false;
        if (route.collapse) {
          const filteredCollapse = filterAvailableRoutes(route.collapse);
          return filteredCollapse.length > 0;
        }
        return true;
      })
      .map((route) => {
        if (!route.collapse) return route;
        return {
          ...route,
          collapse: filterAvailableRoutes(route.collapse),
        };
      });

  const getRoutes = (allRoutes) => {
    const availableRoutes = filterAvailableRoutes(allRoutes);
    return availableRoutes.map((route) => {
      if (route.collapse) return getRoutes(route.collapse);
      if (route.route) {
        return <Route path={route.route} element={route.component} key={route.key} />;
      }
      return null;
    });
  };

  const routeConfig = buildRoutes();
  const availableRoutes = filterAvailableRoutes(routeConfig);
  const currentUser = getCurrentUser();
  const fallbackPath = currentUser?.token ? getDashboardPath(currentUser.role) : landingPageRoute.route;

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={() => setOpenConfigurator(dispatch, !openConfigurator)}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  const appContent = (
    <>
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={
              (transparentSidenav && !darkMode) || whiteSidenav ? brandLogo : brandLogo
            }
            brandName="TIALAI"
            routes={availableRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {configsButton}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(routeConfig)}
        <Route path={landingPageRoute.route} element={landingPageRoute.component} />
        <Route path={signInCoverRoute.route} element={signInCoverRoute.component} />
        <Route path={signUpCoverRoute.route} element={signUpCoverRoute.component} />
        <Route path={detailLayananRoute.route} element={detailLayananRoute.component} />
        <Route path={detailPengajuanRoute.route} element={detailPengajuanRoute.component} />
        <Route path={verifikasiPublikRoute.route} element={verifikasiPublikRoute.component} />
        <Route path="*" element={<Navigate to={fallbackPath} replace />} />
      </Routes>
    </>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {appContent}
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {appContent}
    </ThemeProvider>
  );
}
